import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api, errorMessage } from '../api.js'
import { useToast } from '../../components/ui/Toast.jsx'
import { qk } from './keys.js'

// Saved edits for stateful answer widgets, per conversation:
// { [toolCallId]: state }. Widgets keep their own local value while the user
// works (see useWidgetState), so the cache only needs to follow along.

export function useWidgetStates(conversationId, { enabled = true } = {}) {
  return useQuery({
    queryKey: qk.widgets(conversationId),
    queryFn: ({ signal }) => api(`/conversations/${conversationId}/widgets`, { signal }).then((d) => d.states ?? {}),
    enabled: Boolean(conversationId) && enabled,
    staleTime: 30_000,
    retry: false,
  })
}

/** Returns save(toolCallId, state). Optimistic; a failed save restores the cache and says so. */
export function useSaveWidgetState(conversationId) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const mutation = useMutation({
    mutationFn: ({ toolCallId, state }) =>
      api(`/conversations/${conversationId}/widgets/${encodeURIComponent(toolCallId)}`, {
        method: 'PUT',
        body: { state },
      }),
    onMutate: async ({ toolCallId, state }) => {
      const key = qk.widgets(conversationId)
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData(key)
      queryClient.setQueryData(key, (data) => ({ ...(data ?? {}), [toolCallId]: state }))
      return { rollback: () => queryClient.setQueryData(key, previous) }
    },
    onError: (error, _vars, context) => {
      context?.rollback()
      toast.error(`Your change wasn't saved — ${errorMessage(error)}`)
    },
  })
  const { mutate } = mutation
  return useCallback((toolCallId, state) => mutate({ toolCallId, state }), [mutate])
}
